import { currentBrandCountriesAndLang, defaultBrandLang, chooseBrandLang } from '@b2ag/app-brands/src/country-lang'
import type {
  RouteLocationResolved,
  RouteLocationNormalizedLoaded,
  RouteLocationAsString,
  RouteLocationAsRelative,
  RouteLocationAsPath,
} from 'vue-router'

function splitPathInCountryAndLang(path: string) {
  const splitPath = path.split('/')
  const country = splitPath[1]
  const lang = splitPath[2]
  return { country, lang }
}

export function identifyCountryWithPath(path: string) {
  const countryList = currentBrandCountriesAndLang()
    .map((countryAndLang) => countryAndLang.country)
    .filter((value, index, array) => array.indexOf(value) === index)

  const identifiedCountry = splitPathInCountryAndLang(path).country

  if (!countryList.includes(identifiedCountry)) return

  return identifiedCountry
}

export function identifyLangWithPath(path: string) {
  const identifiedCountry = identifyCountryWithPath(path)
  if (!identifiedCountry) return

  const langList = currentBrandCountriesAndLang()
    .filter((countryAndLang) => countryAndLang.country === identifiedCountry)
    .map((countryAndLang) => countryAndLang.lang)

  const identifiedLang = splitPathInCountryAndLang(path).lang

  if (!langList.includes(identifiedLang)) return

  return identifiedLang
}

export function getI18nBaseParams(path: string) {
  const { country: defaultCountry, lang: defaultLang } = currentBrandCountriesAndLang()[0]
  const countryInPath = identifyCountryWithPath(path)
  const langInPath = identifyLangWithPath(path)

  if (countryInPath) {
    if (langInPath && countryInPath === defaultCountry && langInPath !== defaultLang)
      return {
        base: `/${countryInPath}/${langInPath}`,
      }

    if (countryInPath !== defaultCountry)
      return {
        base: `/${countryInPath}${langInPath ? '/' + langInPath : ''}`,
      }
  }

  const doRedirectToRoot = countryInPath === defaultCountry

  return {
    base: '/',
    redirection: doRedirectToRoot
      ? {
          from: `/${countryInPath}${langInPath ? '/' + langInPath : ''}`,
          to: '/',
        }
      : undefined,
  }
}

export function getLocaleForI18n(path: string) {
  const country = identifyCountryWithPath(path)

  if (country && path !== '/') {
    let lang = identifyLangWithPath(path)

    if (!lang)
      lang = currentBrandCountriesAndLang().filter((countryAndLang) => countryAndLang.country === country)[0].lang

    return `${lang}-${country?.toUpperCase()}`
  }
  return defaultBrandLang()
}

export function checkAndRedirectIfUserIsNotOnCorrectLocalizedRoute(localesAvailablesForUser?: string[]) {
  if (!localesAvailablesForUser || localesAvailablesForUser?.includes(getLocaleForI18n(window.location.pathname)))
    return
  const currentBasePath = getI18nBaseParams(window.location.pathname).base
  const basePath = identifyPathWithLocale(localesAvailablesForUser[0])
  const currentOrigin = window.location.origin
  const routerBasePathToDelete = currentBasePath === '/' ? undefined : currentBasePath

  let currentPathname = window.location.pathname

  if (routerBasePathToDelete) currentPathname = currentPathname.split(routerBasePathToDelete)[1]

  window.location.replace(`${currentOrigin}${basePath}${currentPathname}`)
}

export function identifyPathWithLocale(locale) {
  const country = locale.slice(3).toLowerCase()
  const lang = locale.slice(0, 2)
  return `/${country}/${lang}`
}

export function getAliasForRouter(currentAppBrand: string, path: string = '/') {
  const langList = chooseBrandLang(currentAppBrand)
  const aliasList: string[] = []

  for (const locale of langList) {
    const country = locale.slice(3).toLowerCase()
    const lang = locale.slice(0, 2)

    // Add country variant
    aliasList.push(`/${country}${path}`)
    // Add country/lang variant if it's not default US-EN
    aliasList.push(`/${country}/${lang}${path}`)
  }

  // Remove duplicates
  return [...new Set(aliasList)]
}

export function localizeRoute(
  to: Record<string, any> | string,
  path: string,
  routerResolve: (
    to: RouteLocationAsString | RouteLocationAsRelative | RouteLocationAsPath,
    currentLocation?: RouteLocationNormalizedLoaded,
  ) => RouteLocationResolved,
): Record<string, any> | string {
  const { base } = getI18nBaseParams(path)
  let finalTo: Record<string, any> | string = {
    fullPath: (base + routerResolve(to).fullPath).replace(/\/\//g, '/'),
  }

  // If the route is found, we use the resolved route with basePath
  if (routerResolve(finalTo.fullPath).name !== 'not-found') {
    if (typeof to === 'object')
      finalTo = {
        ...to,
        ...finalTo,
      }

    finalTo = {
      ...finalTo,
      name: undefined,
      path: finalTo.fullPath,
      href: finalTo.fullPath,
    }
  }
  // If we dont't have a known route, we use the original params
  else finalTo = to

  return finalTo
}

export function redirectToWithLocal(locale: string, path: string) {
  const localizedPath = identifyPathWithLocale(locale)
  const hasLangInPath = Boolean(identifyLangWithPath(path))
  const pathWithoutLocale = hasLangInPath ? path.replace(/^\/[a-z]{2}\/[a-z]{2}/, '') : path.replace(/^\/[a-z]{2}/, '')
  window.location.href = window.location.origin + localizedPath + pathWithoutLocale
}
